import {Component, Inject, OnDestroy, OnInit, Optional, ViewEncapsulation} from "@angular/core";
import {MAT_DIALOG_DATA, MatDialog, MatDialogModule, MatDialogRef} from "@angular/material/dialog";
import { FetchUserDataOnDashboardService } from "../../dashboard/Services/fetch-user-data-on-dashboard.service";
import { UserPreferencesService } from "../../../modules/shared/Services/user-preferences.service";
import { Organization } from "../../../models/organization";
import { Subject } from "rxjs";
import {debounceTime, map, switchMap, takeUntil} from "rxjs/operators";
import { DashboardService } from "../../dashboard/Services/dashboard.service";
import { MatInputModule } from "@angular/material/input";
import {FormsModule, ReactiveFormsModule} from "@angular/forms";
import { MatButtonModule } from "@angular/material/button";
import {MatSelectModule} from "@angular/material/select";
import {MatIconModule} from "@angular/material/icon";
import {OrganizationService} from "../../../modules/shared/Services/organization.service";
import {HandleErrorService} from "../../../services/handle-error-service";
import {NgForOf, NgIf} from "@angular/common";
import {Constants} from "../../../models/constants";
import {JitsiService} from "../../../services/jitsi.service";
import {SharedService} from "../../../services/shared.service";
import {
    ActiveUpcomingMeetingsResponse, DEFAULT_MEETING_TITLE, JITSI_MAX_VISIBLE_ATTENDEES,
    JitsiActiveMeetingModel,
    JitsiParticipantModel, MEETING_15_MINUTES_BEFORE_ALREADY_SCHEDULED, ParticipantViewModel, ScheduledMeetingModel
} from "../../../models/jitsi-meeting";
import {CreateMeetingService} from "../../../modules/meetings-workflow/services/create-meeting.service";
import {
    Meeting,
    MeetingConstants
} from "../../../modules/meetings-workflow/models/meeting.model";
import {format} from "date-fns";
import {LucideAngularModule} from "lucide-angular";
import {MatTooltip} from "@angular/material/tooltip";
import {ProfileFetchType} from "../../../modules/shared/Models/shared-data-models.model";
import {UserDetailsService} from "../../../services/UserAccountRealted/user-details.service";
import {MEETING_VALIDATION_CONSTANTS} from "../../../form-validation.constants";
import {OrgBuProjectTeamStructureComponent} from "../../../modules/shared/Components/org-bu-project-team-structure/org-bu-project-team-structure.component";
import {SCREEN_NAMES} from "../../../modules/shared/Data/task-constants-and-data.model";
import {OrgBUProjectTeamStructure} from "../../../models/custom-models/org-buproject-team-structure";
import {StatsService} from "../../../services/stats.service";
import {OrgBuProjectTeamStructureService} from "../../../modules/shared/Services/org-bu-project-team-structure.service";
import {EntityInfo} from "../../../models/custom-models/entity-info";
import {MeetingScheduleWarningDialogComponent} from "../meeting-schedule-warning-dialog/meeting-schedule-warning-dialog.component";
import {AiInsDetectionResponse} from "../voice-instructions/Models/ai-model.model";

@Component({
    selector: "app-get-jitsi-meeting-name-dialogbox",
    templateUrl: "./get-jitsi-meeting-name.component.html",
    styleUrls: ["./get-jitsi-meeting-name.component.scss"],
    encapsulation: ViewEncapsulation.None,
    imports: [MatDialogModule, MatInputModule, FormsModule, MatButtonModule, MatSelectModule, MatIconModule, NgForOf, NgIf, LucideAngularModule, MatTooltip, ReactiveFormsModule, OrgBuProjectTeamStructureComponent],
    standalone: true
})
export class GetJitsiMeetingNameComponent implements OnInit, OnDestroy {
    meetingName: string = "";
    orgList: Organization[] = [];
    selectedOrgId: number;
    selectedTeamId: number | null = null;
    selectedProjectId: number | null = null;
    selectedBUId: number | null = null;
    activeMeetingDetails: JitsiActiveMeetingModel;
    isMeetingActive: boolean = true;
    private destroy$ = new Subject<void>();
    meetingId: number = 0;
    teamId: number = 0;
    projectId: number = 0;
    isOrganiser: boolean = false;
    meetingKey: string;
    meetingNameChanges = new Subject<string>();
    duration: number = MeetingConstants.JITSI_JOIN_DURATION; // minutes
    meetingTitle: string = this.getDefaultMeetingTitle();
    isFetchingActiveMeetingData: boolean = false;
    organizer: ParticipantViewModel | null = null;
    attendees: ParticipantViewModel[] = [];
    attendeesToShowWithProfileView: ParticipantViewModel[] = [];
    additionalAttendeesCount: number = 0;
    MAX_VISIBLE_ATTENDEES: number = JITSI_MAX_VISIBLE_ATTENDEES;
    private fetchActiveMeeting$ = new Subject<void>();
    dataSource: OrgBUProjectTeamStructure[] = [];
    selectedEntityName: string = '';
    selectedEntityInfo: EntityInfo | undefined;

    scheduledMeetingTimeText: string | null = null;
    scheduledMeetingTime: Date | null = null;
    isMeetingScheduledToday: boolean = false;
    maxDuration: number | null = null;
    isMeetingNameInvalid: boolean = false;
    private meetingNamePattern = /^[a-zA-Z0-9_]+$/;
    private isMeetingStartingIn15Minutes: boolean = false;
    private aiResponse: AiInsDetectionResponse | null = null;

    constructor(
        private dialogRef: MatDialogRef<GetJitsiMeetingNameComponent>,
        private dialog: MatDialog,
        private fetchUserDetails: FetchUserDataOnDashboardService,
        private userPreferences: UserPreferencesService,
        private dashboardService: DashboardService,
        private orgService: OrganizationService,
        private handleErr: HandleErrorService,
        private userPreferencesService: UserPreferencesService,
        private jitsiService: JitsiService,
        private sharedService: SharedService,
        private createMeetingService: CreateMeetingService,
        private userDetailsService: UserDetailsService,
        private statsService: StatsService,
        private orgBuProjectTeamStructureService: OrgBuProjectTeamStructureService,
        @Optional() @Inject(MAT_DIALOG_DATA) private dialogData: any,
    ) {}

    private getDefaultMeetingTitle(): string {
        const todayFormatted = format(new Date(), 'dd MMM yyyy');
        return `${DEFAULT_MEETING_TITLE}${todayFormatted}`;
    }


    findSelectedOrg(orgs: ReadonlyArray<Organization>): string | undefined {
        if (!orgs?.length) {
            return; // nothing to choose from
        }
        const selectedOrg =
            // Whatever the app says is globally selected
            this.globalSelectedEntity(orgs) ??
            // …otherwise the user’s saved preference (if any)
            (() => {
                const prefId = this.userPreferences.getUserPreferredOrgId();
                return prefId != null ? orgs.find((o) => Number(o.orgId) === prefId) : undefined;
            })() ??
            // …otherwise fall back to the first org in the list
            orgs[0];

        if (selectedOrg) {
            this.selectedOrgId = Number(selectedOrg.orgId);
        }

        return selectedOrg ? `${selectedOrg.organizationName}_${selectedOrg.orgId}` : undefined;
    }

    ngOnInit(): void {
        if (this.dialogData){
            this.aiResponse = this.dialogData.aiResponse;

            if (this.aiResponse?.title){
                this.meetingTitle =  this.aiResponse.title;
            }

            if (this.aiResponse?.estimate){
                this.duration = this.aiResponse.estimate;
            }
        }

        this.getInitialData();

        // This will kill any previous api call if any when the meeting room name changes
        this.fetchActiveMeeting$
            .pipe(
                takeUntil(this.destroy$),
                switchMap(() => {
                    this.isFetchingActiveMeetingData = true;
                    return this.jitsiService.getActiveJitsiMeeting(
                        this.selectedOrgId,
                        this.meetingKey
                    );
                })
            )
            .subscribe({
                next: (res: { data: ActiveUpcomingMeetingsResponse }) => {
                    this.isFetchingActiveMeetingData = false;

                    if (!res?.data) {
                        this.isMeetingActive = false;
                        return;
                    }

                    const { ongoingMeetings, upcomingMeetings } = res.data;

                    if (ongoingMeetings?.length > 0) {
                        const meeting = ongoingMeetings[0];

                        this.activeMeetingDetails = meeting;
                        this.meetingName = meeting.room;
                        this.meetingId = Number(meeting.meetingId);
                        this.isMeetingActive = true;
                        this.meetingTitle = meeting.title;

                        this.processParticipants(meeting.participants);
                    } else {
                        this.isMeetingActive = false;
                    }

                    if (!this.isMeetingActive && upcomingMeetings?.length > 0) {
                        this.handleUpcomingMeeting(upcomingMeetings);
                    } else {
                        this.isMeetingScheduledToday = false;
                        this.scheduledMeetingTimeText = null;
                    }
                },
                error: (err) => {
                    this.isFetchingActiveMeetingData = false;
                    this.isMeetingActive = false;
                    this.handleErr.handleError(err);
                }
            });

        this.fetchUserDetails.userOrganisations$
            .pipe(
                takeUntil(this.destroy$),
                map((organizations) => {
                    return this.findSelectedOrg(organizations);
                }),
            )
            .subscribe({
                next: (meetingName) => {
                    if (meetingName) {
                        this.meetingName = meetingName;
                        this.meetingKey = meetingName;
                        if(this.aiResponse?.meeting_id){
                            this.meetingName = this.aiResponse.meeting_id;
                        }
                        this.validateMeetingName(this.meetingName);
                    }
                },
                error: (err) => {
                    this.handleErr.handleError(err);
                },
            });

        let input = document.getElementById("meetingNameInputId");
        input?.addEventListener("keypress", function (event) {
            // If the user presses the "Enter" key on the keyboard
            if (event.key === "Enter") {
                // Cancel the default action, if needed
                event.preventDefault();
                // Trigger the button element with a click
                document.getElementById("getMeetingNameBtnId")?.click();
            }
        });

        this.orgService
            .getUserOrganizationList()
            .subscribe({
                next: (res) => {
                    this.orgList = res.data.filter((org) =>
                        org.orgId != Constants.EntityTypes.PERSONAL_ORG_ID.toString());
                },
                error: (err) => {
                    this.handleErr.handleError(err);
                },
            });

        this.meetingNameChanges.pipe(
            debounceTime(500),
            takeUntil(this.destroy$)
        ).subscribe(name => {
            if (this.isMeetingNameInvalid) {
                return;
            }

            this.resetParticipants();
            if (this.selectedOrgId && name.trim().length > 0) {
                this.meetingKey = name.trim();
                this.fetchActiveMeeting$.next();
            }
        });
    }

    getInitialData() {
        const orgBuProjTeamsStructure = this.statsService.getOrgTeamDropdownStructureV2(
            this.sharedService.getLoggedInUserPrimaryEmail(),
            SCREEN_NAMES.DASHBOARD,
        );
        orgBuProjTeamsStructure.subscribe({
            next: (response) => {
                this.dataSource = this.orgBuProjectTeamStructureService.createOrgBuProjectTeamStructure(
                    response.data.organizations,
                );

                this.handleDefaultOrg();
            },
            error: (error) => {
                this.handleErr.handleError(error);
            },
        });
    }

    handleSelectedEntityNew(entity?: OrgBUProjectTeamStructure): void {
        if (!entity) {
            this.selectedEntityInfo = undefined;
            this.selectedEntityName = '';
            this.resetEntityId();
            return;
        }

        const {
            entityType,
            entityTypeId,
            name,
            id,
            orgId,
            buId,
            projectId,
            teamId
        } = entity;

        this.selectedEntityInfo = {
            entityType,
            entityName: name,
            entityId: id,
            entityTypeId
        } as EntityInfo;

        this.selectedEntityName = name;

        // reset everything first
        this.resetEntityId();

        // org is always known
        this.selectedOrgId = orgId;
        this.selectedBUId = buId ?? null;
        this.selectedProjectId = projectId ?? null;
        this.selectedTeamId = teamId ?? null;
    }

    resetEntityId(){
        this.selectedTeamId = null;
        this.selectedProjectId = null;
        this.selectedBUId = null;
    }

    onMeetingNameChange(value: string): void {
        this.meetingName = value;

        this.validateMeetingName(value);

        if (this.isMeetingNameInvalid) {
            return;
        }

        this.meetingNameChanges.next(value);
    }

    validateMeetingName(name: string): void {
        if (!name) {
            this.isMeetingNameInvalid = false;
            return;
        }

        this.isMeetingNameInvalid = !this.meetingNamePattern.test(name);
    }

    private handleUpcomingMeeting(upcomingMeetings: ScheduledMeetingModel[]): void {
        if (!upcomingMeetings || upcomingMeetings.length === 0) {
            this.resetScheduledState();
            return;
        }

        const meeting = upcomingMeetings[0];
        this.scheduledMeetingTime = new Date(meeting.startDateTime);
        const now = new Date();

        const isToday =
            this.scheduledMeetingTime.getDate() === now.getDate() &&
            this.scheduledMeetingTime.getMonth() === now.getMonth() &&
            this.scheduledMeetingTime.getFullYear() === now.getFullYear();

        if (!isToday) {
            this.resetScheduledState();
            return;
        }

        // buffer of 15 minutes
        const BUFFER_MINUTES = MEETING_15_MINUTES_BEFORE_ALREADY_SCHEDULED;

        const diffMs = this.scheduledMeetingTime.getTime() - now.getTime();
        const diffMinutes = Math.floor(diffMs / (1000 * 60)) - BUFFER_MINUTES;

        if (diffMinutes > 0) {
            this.isMeetingScheduledToday = true;
            this.scheduledMeetingTimeText =
                `Meeting is scheduled at ${format(this.scheduledMeetingTime, 'hh:mm a')}`;

            this.maxDuration = diffMinutes;

            // clamp current duration if needed
            if (this.duration > this.maxDuration) {
                this.duration = this.maxDuration;
            }
        } else {
            // no time left to create meeting
            this.isMeetingScheduledToday = true;
            this.isMeetingStartingIn15Minutes = true;
            this.scheduledMeetingTimeText =
                `This room has another meeting scheduled at ${format(
                    this.scheduledMeetingTime,
                    'hh:mm a'
                )}, starting in less than 15 minutes. Please create a different room.`;


        }
    }

    private resetScheduledState(): void {
        this.isMeetingScheduledToday = false;
        this.scheduledMeetingTimeText = null;
        this.maxDuration = null;
    }

    private processParticipants(participants: JitsiParticipantModel[] = []): void {
        const mappedParticipants: ParticipantViewModel[] = participants.map(p => ({
            ...p,
            image: null
        }));

        this.organizer = mappedParticipants.find(p => p.isOrganiser) ?? null;
        this.attendees = mappedParticipants.filter(p => !p.isOrganiser);

        this.attendeesToShowWithProfileView =
            this.attendees.slice(0, this.MAX_VISIBLE_ATTENDEES);

        this.additionalAttendeesCount =
            Math.max(this.attendees.length - this.MAX_VISIBLE_ATTENDEES, 0);

        // fetch images in background
        this.fetchParticipantImages(mappedParticipants);
    }

    private fetchParticipantImages(participants: ParticipantViewModel[]): void {
        participants.forEach(participant => {
            if (!participant.email) return;

            this.userDetailsService
                .getProfileImage(participant.email, ProfileFetchType.USER_EMAIL)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (blob: Blob) => {
                        if (blob && blob.size > 0) {
                            this.userDetailsService
                                .blobToBase64(blob)
                                .then(base64 => participant.image = base64);
                        }
                    },
                    error: () => {
                        participant.image = null;
                    }
                });
        });
    }


    getAttendeeNamesTooltip(): string {
        return this.attendees
            .slice(this.MAX_VISIBLE_ATTENDEES)
            .map(a => a.name)
            .join(', ');
    }

    private resetParticipants(): void {
        this.organizer = null;
        this.attendees = [];
        this.attendeesToShowWithProfileView = [];
        this.additionalAttendeesCount = 0;
    }



    createNewMeeting(): void {
        const accountId = this.sharedService.getUserAccountIdWithOrgId(this.selectedOrgId);
        if (!accountId) return;
        const startDateTime = format(new Date(), "yyyy-MM-dd'T'HH:mm:ss");
        const meetingDetails = new Meeting(
            MeetingConstants.MEETING_TYPE_ONLINE, // meetingType
            this.meetingTitle,                     // title
            accountId,                            // organizerAccountId
            accountId,                            // createdAccountId
            this.selectedOrgId,                   // orgId
            this.selectedTeamId,                  // teamId
            startDateTime,                        // startDateTime
            this.meetingName                      // meetingKey
        );

        // mark as adhoc
        meetingDetails.isAdhoc = true;
        meetingDetails.duration = this.duration;
        meetingDetails.buId = this.selectedBUId;
        meetingDetails.projectId = this.selectedProjectId;

        this.sharedService.showSpinner();

        if (this.isMeetingScheduledToday){
            // we have to show a dialog box for confirmation
            this.sharedService.hideSpinner();
            const dialogRef = this.dialog.open(
                MeetingScheduleWarningDialogComponent,
                {
                    width: "420px",
                    disableClose: true,
                    data: {
                        time: format(this.scheduledMeetingTime!, "hh:mm a"),
                    }
                }
            );

            dialogRef.afterClosed().subscribe((confirmed: boolean) => {
                if (confirmed) {
                    this.createMeetingService.createMeeting(meetingDetails, accountId)
                        .pipe(takeUntil(this.destroy$))
                        .subscribe({
                            next: (res) => {
                                this.sharedService.hideSpinner();
                                if (res?.data) {
                                    this.meetingId = res.data.meetingId!;
                                    this.isOrganiser = true;
                                    this.joinMeeting();
                                }
                            },
                            error: (err) => {
                                this.sharedService.hideSpinner();
                                this.handleErr.handleError(err);
                            }
                        });
                }
            });

            return;

        }else{
            this.createMeetingService.createMeeting(meetingDetails, accountId)
                .pipe(takeUntil(this.destroy$))
                .subscribe({
                    next: (res) => {
                        this.sharedService.hideSpinner();

                        if (res?.data) {
                            this.meetingId = res.data.meetingId!;
                            this.isOrganiser = true;
                            this.joinMeeting();
                        }
                    },
                    error: (err) => {
                        this.sharedService.hideSpinner();
                        this.handleErr.handleError(err);
                    }
                });
        }
    }

    isCreateMeetingDisabled():boolean {
        if (this.duration == 0 || (this.maxDuration && this.duration > this.maxDuration)) {
            return true;
        }
        return !this.meetingName || !this.selectedOrgId || this.isMeetingNameInvalid || !this.meetingTitle.trim() || this.isMeetingStartingIn15Minutes;
    }

    isJoinMeetingDisabled():boolean {
        return this.isFetchingActiveMeetingData || !this.meetingName || !this.selectedOrgId || this.isMeetingNameInvalid;
    }

    private handleDefaultOrg() {
        let targetEntity: OrgBUProjectTeamStructure | null = null;

        if (this.aiResponse) {

            if (this.aiResponse.teamName) {
                targetEntity =
                    this.orgBuProjectTeamStructureService.findBestMatchEntity(
                        this.dataSource,
                        null,
                        this.aiResponse.teamName
                    );
            }

            if (!targetEntity && this.aiResponse.projectName) {
                targetEntity =
                    this.orgBuProjectTeamStructureService.findBestMatchEntity(
                        this.dataSource,
                        null,
                        this.aiResponse.projectName
                    );
            }

            if (!targetEntity && this.aiResponse.organizationName) {
                targetEntity =
                    this.orgBuProjectTeamStructureService.findBestMatchEntity(
                        this.dataSource,
                        null,
                        this.aiResponse.organizationName
                    );
            }
        }

        // existing fallbacks unchanged
        if (!targetEntity && this.orgList?.length === 1) {
            const orgId = Number(this.orgList[0].orgId);
            targetEntity =
                this.dataSource.find(
                    e => e.id === orgId && e.entityTypeId === Constants.EntityTypes.ORG
                ) || null;
        }

        if (!targetEntity) {
            // get the orgId of selected entity to set that entity org for meetings
            const selectedOrgFromDashboard: number | null = this.dashboardService.getOrgIdOfSelectedEntity();

            if (selectedOrgFromDashboard) {
                targetEntity =
                    this.dataSource.find(e => e.id === selectedOrgFromDashboard  && e.entityTypeId === Constants.EntityTypes.ORG) || null;
            }
        }

        if (!targetEntity) {
            const prefOrgId = this.userPreferencesService.getUserPreferredOrgId();
            if (prefOrgId) {
                targetEntity =
                    this.dataSource.find(
                        e => e.id === prefOrgId && e.entityTypeId === Constants.EntityTypes.ORG
                    ) || null;
            }
        }

        if (!targetEntity && this.dataSource.length > 0) {
            targetEntity =
                this.dataSource.find(e => e.entityTypeId === Constants.EntityTypes.ORG) || null;
        }

        if (targetEntity) {
            this.handleSelectedEntityNew(targetEntity);

            if (!this.isMeetingNameInvalid) {
                this.fetchActiveMeeting$.next();
            }
        }
    }


    joinMeeting() {
        this.meetingName = this.meetingName.trim();
        if(!this.selectedOrgId) return;
        if (this.meetingName.length > 0) {
            let accountId = this.sharedService.getUserAccountIdWithOrgId(this.selectedOrgId);
            this.sharedService.showSpinner();
            if(accountId) {
                const requestBody = {
                    meetingId: this.meetingId,
                    teamId: this.teamId,
                    projectId: this.projectId,
                    isOrganiser: this.isOrganiser
                };

                this.jitsiService.getCreateJitsiMeetingToken(accountId, this.meetingName, requestBody)
                    .subscribe({
                        next: (res) => {
                            this.sharedService.hideSpinner();
                            this.handleMeetingDialogClose(res.data);
                        },
                        error: (err) => {
                            this.sharedService.hideSpinner();
                            this.handleErr.handleError(err);
                        }
                    })
            }
        }
    }

    handleMeetingDialogClose(meetingToken: string){
        const isScheduled = this.isMeetingScheduledToday;

        this.dialogRef.close(
            {
                meetingName: this.meetingName,
                meetingToken: meetingToken,
                isAnotherMeetingScheduledToday: isScheduled
            }
        );
        this.ngOnDestroy();
    }

    globalSelectedEntity(organizations: ReadonlyArray<Organization>): Organization | undefined {
        const globalEntityInfo: number | null | undefined = this.dashboardService.getOrgIdOfSelectedEntity();
        if (!globalEntityInfo) return undefined;
        return organizations.find((org) => Number(org.orgId) === globalEntityInfo);
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    closeDialog() {
        this.dialogRef.close(null);
    }

    protected readonly MEETING_VALIDATION_CONSTANTS = MEETING_VALIDATION_CONSTANTS;
}
